import {
    Activity,
    BellOff,
    Building2,
    CalendarClock,
    CalendarDays,
    CalendarRange,
    CalendarX2,
    Layers3,
    Target
} from 'lucide-react'
import { DashboardPipeline } from './dashboard.types'

const IndicatorShell = ({ children }: { children: React.ReactNode }) => (
    <div className='flex h-full flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs dark:border-gray-700 dark:bg-white/[0.03]'>
        {children}
    </div>
)

export const PortfolioIndicator = ({ pipeline }: { pipeline: DashboardPipeline }) => {
    const busiestStep = pipeline.steps.reduce<(typeof pipeline.steps)[number] | null>((current, step) => {
        if (!current || step.count > current.count) return step
        return current
    }, null)
    const concentration =
        busiestStep && pipeline.totalBusinesses > 0
            ? Math.round((busiestStep.count / pipeline.totalBusinesses) * 100)
            : 0
    const occupiedSteps = pipeline.steps.filter(step => step.count > 0).length

    return (
        <IndicatorShell>
            <div className='flex items-start justify-between gap-3'>
                <div>
                    <p className='text-sm font-semibold text-gray-800 dark:text-white/90'>Fluxo da carteira</p>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Distribuição atual do pipeline</p>
                </div>
                <span className='rounded-xl bg-brand-50 p-2.5 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'>
                    <Layers3 className='h-5 w-5' />
                </span>
            </div>

            <div className='mt-5 flex items-end gap-2'>
                <strong className='text-3xl font-semibold tracking-tight text-gray-900 dark:text-white'>
                    {pipeline.totalBusinesses.toLocaleString('pt-BR')}
                </strong>
                <span className='pb-1 text-sm text-gray-500 dark:text-gray-400'>negócios</span>
            </div>

            <div className='mt-5 space-y-3 border-t border-gray-100 pt-4 dark:border-gray-800'>
                <div className='flex items-center justify-between gap-3 text-xs'>
                    <span className='flex items-center gap-2 text-gray-500 dark:text-gray-400'>
                        <Building2 className='h-4 w-4' /> Empresas na carteira
                    </span>
                    <strong className='text-gray-800 dark:text-gray-100'>{pipeline.totalCompanies}</strong>
                </div>
                <div className='flex items-center justify-between gap-3 text-xs'>
                    <span className='flex items-center gap-2 text-gray-500 dark:text-gray-400'>
                        <Target className='h-4 w-4' /> Etapas com negócios
                    </span>
                    <strong className='text-gray-800 dark:text-gray-100'>
                        {occupiedSteps} de {pipeline.steps.length}
                    </strong>
                </div>
                <div className='rounded-xl bg-gray-50 p-3 dark:bg-white/[0.04]'>
                    <div className='flex items-center justify-between gap-3 text-xs'>
                        <span className='truncate text-gray-500 dark:text-gray-400'>Maior concentração</span>
                        <strong className='shrink-0 text-gray-800 dark:text-gray-100'>{concentration}%</strong>
                    </div>
                    <p className='mt-1 truncate text-sm font-semibold text-gray-800 dark:text-gray-100'>
                        {busiestStep?.label || 'Sem negócios no pipeline'}
                    </p>
                </div>
            </div>
        </IndicatorShell>
    )
}

export const FollowUpIndicator = ({ pipeline }: { pipeline: DashboardPipeline }) => {
    const { onTrack, overdue } = pipeline.health
    const totalFollowUps = onTrack + overdue
    const onTrackPercentage = totalFollowUps > 0 ? Math.round((onTrack / totalFollowUps) * 100) : 0
    const overduePercentage = totalFollowUps > 0 ? 100 - onTrackPercentage : 0
    const coveragePercentage =
        pipeline.totalBusinesses > 0 ? Math.round((totalFollowUps / pipeline.totalBusinesses) * 100) : 0
    const chartBackground = `conic-gradient(#12B76A 0 ${onTrackPercentage}%, #F04438 ${onTrackPercentage}% 100%)`
    const footerMessage =
        overdue > 0
            ? 'Acompanhamentos vencidos exigem prioridade para manter o fluxo comercial ativo.'
            : totalFollowUps > 0
            ? 'Todos os lembretes abertos deste pipeline estão em dia.'
            : 'Negócios sem lembrete aberto não entram no gráfico de saúde.'

    return (
        <IndicatorShell>
            <div className='flex items-start justify-between gap-3'>
                <div>
                    <p className='text-sm font-semibold text-gray-800 dark:text-white/90'>Saúde do acompanhamento</p>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>Lembretes abertos da nota vigente</p>
                </div>
                <span className='rounded-xl bg-brand-50 p-2.5 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300'>
                    <Activity className='h-5 w-5' />
                </span>
            </div>

            {totalFollowUps > 0 ? (
                <div className='my-auto flex items-center gap-5 py-5'>
                    <div
                        className='relative grid h-24 w-24 shrink-0 place-items-center rounded-full'
                        style={{ background: chartBackground }}
                        role='img'
                        aria-label={`${onTrackPercentage}% em dia e ${overduePercentage}% vencidos`}
                    >
                        <div className='grid h-[72px] w-[72px] place-items-center rounded-full bg-white dark:bg-gray-900'>
                            <div className='text-center'>
                                <strong className='block text-xl font-semibold text-success-600 dark:text-success-400'>
                                    {onTrackPercentage}%
                                </strong>
                                <span className='text-[10px] text-gray-500 dark:text-gray-400'>em dia</span>
                            </div>
                        </div>
                    </div>

                    <div className='min-w-0 flex-1 space-y-3'>
                        <div className='flex items-center justify-between gap-3 text-xs'>
                            <span className='flex items-center gap-2 font-medium text-success-700 dark:text-success-400'>
                                <span className='h-2.5 w-2.5 rounded-full bg-success-500' /> Em dia
                            </span>
                            <strong className='text-success-600 dark:text-success-400'>{onTrack}</strong>
                        </div>
                        <div className='flex items-center justify-between gap-3 text-xs'>
                            <span className='flex items-center gap-2 font-medium text-error-700 dark:text-error-400'>
                                <span className='h-2.5 w-2.5 rounded-full bg-error-500' /> Vencidos
                            </span>
                            <strong className='text-error-600 dark:text-error-400'>{overdue}</strong>
                        </div>
                    </div>
                </div>
            ) : (
                <div className='my-auto flex flex-col items-center gap-2 py-6 text-center'>
                    <span className='rounded-xl bg-gray-100 p-3 text-gray-400 dark:bg-white/[0.06] dark:text-gray-500'>
                        <BellOff className='h-5 w-5' />
                    </span>
                    <p className='text-sm font-semibold text-gray-800 dark:text-gray-100'>Nenhum lembrete aberto</p>
                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                        Registre notas com lembrete para acompanhar os negócios deste pipeline.
                    </p>
                </div>
            )}

            <div className='border-t border-gray-100 pt-4 dark:border-gray-800'>
                <div className='flex items-center justify-between gap-3 text-xs'>
                    <span className='text-gray-500 dark:text-gray-400'>Cobertura do pipeline</span>
                    <strong className='shrink-0 text-gray-800 dark:text-gray-100'>
                        {totalFollowUps} de {pipeline.totalBusinesses} negócios
                        <span className='font-normal text-gray-400'> · {coveragePercentage}%</span>
                    </strong>
                </div>
                <p className='mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400'>{footerMessage}</p>
            </div>
        </IndicatorShell>
    )
}

export const ReminderAgendaIndicator = ({ pipeline }: { pipeline: DashboardPipeline }) => {
    const { overdue } = pipeline.health
    const { dueToday, dueSoon, dueLater, withoutReminder } = pipeline.agenda
    const needsActionToday = overdue + dueToday

    const tiles = [
        {
            key: 'overdue',
            label: 'Vencidos',
            hint: 'Lembretes em atraso',
            value: overdue,
            icon: <CalendarX2 className='h-4 w-4' />,
            iconClasses: 'bg-error-50 text-error-600 dark:bg-error-500/10 dark:text-error-400'
        },
        {
            key: 'today',
            label: 'Para hoje',
            hint: 'Vencem na data atual',
            value: dueToday,
            icon: <CalendarClock className='h-4 w-4' />,
            iconClasses: 'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400'
        },
        {
            key: 'week',
            label: 'Próximos 7 dias',
            hint: 'Vencem nesta semana',
            value: dueSoon,
            icon: <CalendarRange className='h-4 w-4' />,
            iconClasses: 'bg-brand-50 text-brand-600 dark:bg-brand-500/10 dark:text-brand-400'
        },
        {
            key: 'later',
            label: 'Após 7 dias',
            hint: 'Programados para depois',
            value: dueLater,
            icon: <CalendarDays className='h-4 w-4' />,
            iconClasses: 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300'
        },
        {
            key: 'without',
            label: 'Sem lembrete aberto',
            hint: 'Negócios sem próximo passo',
            value: withoutReminder,
            icon: <BellOff className='h-4 w-4' />,
            iconClasses: 'bg-gray-100 text-gray-600 dark:bg-white/[0.06] dark:text-gray-300'
        }
    ]

    return (
        <IndicatorShell>
            <div className='flex flex-wrap items-start justify-between gap-3'>
                <div>
                    <p className='text-sm font-semibold text-gray-800 dark:text-white/90'>Agenda de lembretes</p>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                        Prazos das notas vigentes do pipeline selecionado
                    </p>
                </div>
                <span
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${
                        needsActionToday > 0
                            ? 'bg-warning-50 text-warning-700 dark:bg-warning-500/10 dark:text-warning-400'
                            : 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-400'
                    }`}
                >
                    <CalendarClock className='h-3.5 w-3.5' />
                    {needsActionToday > 0
                        ? `${needsActionToday} ${needsActionToday === 1 ? 'exige ação hoje' : 'exigem ação hoje'}`
                        : 'Nenhum lembrete vence hoje'}
                </span>
            </div>

            <div className='mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5'>
                {tiles.map(tile => (
                    <div key={tile.key} className='rounded-xl bg-gray-50 p-4 dark:bg-white/[0.04]'>
                        <div className='flex items-center gap-2.5'>
                            <span className={`rounded-lg p-2 ${tile.iconClasses}`}>{tile.icon}</span>
                            <strong className='text-2xl font-semibold tracking-tight text-gray-900 dark:text-white'>
                                {tile.value.toLocaleString('pt-BR')}
                            </strong>
                        </div>
                        <p className='mt-2.5 truncate text-xs font-medium text-gray-700 dark:text-gray-200'>{tile.label}</p>
                        <p className='mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400'>{tile.hint}</p>
                    </div>
                ))}
            </div>
        </IndicatorShell>
    )
}
